/**
 * CR-7090: following a Data Import upload once it runs on a worker.
 *
 * The upload endpoint used to parse and import the file inside the request,
 * so the response WAS the result. Now it records the attempt, queues the
 * import and answers 202 at once with the run id; the outcome lands on that
 * run as the worker finishes. These helpers poll for it. They take the fetch
 * and the sleep as arguments so the page wires in its auth headers and the
 * tests wire in fakes.
 */

/** One upload attempt as the step-runs detail endpoint returns it. */
export interface RunOutcome {
  id: number;
  step: number;
  step_number: number;
  status: string;
  site: number | null;
  site_name: string | null;
  file_name: string;
  file_size: number | null;
  rows_total: number;
  rows_created: number;
  rows_updated: number;
  rows_skipped: number;
  rows_failed: number;
  /** Every reason — the detail endpoint is not previewed. */
  errors: string[];
  error_count: number;
  started_by_name: string | null;
  started_at: string | null;
  completed_at: string | null;
}

/** Where an in-flight upload is, for the dialog's progress line. */
export type UploadPhase = 'uploading' | 'queued' | 'importing';

/** How often the run is read while it is in flight. */
export const POLL_INTERVAL_MS = 3000;

/** After this long the page says the import is taking longer than usual. */
export const SLOW_NOTICE_MS = 5 * 60 * 1000;

/**
 * Upper bound on polling, matched to the worker's 4-hour task limit: a run
 * older than that has been given up on server-side as well.
 */
export const MAX_POLL_MS = 4 * 60 * 60 * 1000;

export const isFinalRunStatus = (status: string): boolean =>
  status === 'completed' || status === 'failed';

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface PollRunOptions {
  intervalMs?: number;
  slowAfterMs?: number;
  maxMs?: number;
  /** Called with the phase each time the run is read, so the dialog follows it. */
  onPhase?: (phase: UploadPhase) => void;
  /** Called once, the first time the run has been in flight past slowAfterMs. */
  onSlow?: () => void;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

/**
 * Read the run until it is completed or failed.
 *
 * @param fetchRun - reads the run; may throw or return null on a transient failure
 * @returns the final run, or null if the budget ran out while it was still in flight
 */
export async function pollRunOutcome(
  fetchRun: () => Promise<RunOutcome | null>,
  options: PollRunOptions = {},
): Promise<RunOutcome | null> {
  const {
    intervalMs = POLL_INTERVAL_MS,
    slowAfterMs = SLOW_NOTICE_MS,
    maxMs = MAX_POLL_MS,
    onPhase,
    onSlow,
    sleep = defaultSleep,
    now = () => Date.now(),
  } = options;
  const startedAt = now();
  let slowSaid = false;
  while (now() - startedAt < maxMs) {
    await sleep(intervalMs);
    let run: RunOutcome | null = null;
    try {
      run = await fetchRun();
    } catch {
      // Transient poll failure — the run is still there; keep waiting.
    }
    if (run) {
      onPhase?.(run.status === 'queued' ? 'queued' : 'importing');
      if (isFinalRunStatus(run.status)) return run;
    }
    if (!slowSaid && now() - startedAt >= slowAfterMs) {
      slowSaid = true;
      onSlow?.();
    }
  }
  return null;
}

/** The fields of a step the dropped-connection poll compares. */
export interface StepStampSource {
  step_number: number;
  status: string;
  started_at: string | null;
  completed_at: string | null;
}

/**
 * Identifies a step record as the server held it before an upload attempt.
 * If the request never reached the server the record keeps its previous
 * timestamps, and reporting it as this attempt's outcome would be a false
 * success or failure.
 */
export const stepStamp = (step: StepStampSource | undefined | null): string | null =>
  step ? `${step.status}|${step.started_at}|${step.completed_at}` : null;

export interface PollStepOptions {
  attempts?: number;
  intervalMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

/**
 * SI-2 / CR-7089: after a network-cut upload, ask the server what happened
 * before reporting anything. Kept for the case where the upload itself (not
 * the import, which no longer runs in the request) outlives the connection.
 *
 * @returns the step once it is completed or failed, and whether the server was
 *   SEEN acting on this attempt — the only licence for a "still processing"
 *   claim if the budget runs out
 */
export async function pollStepOutcome<T extends StepStampSource>(
  fetchSteps: () => Promise<T[] | null>,
  stepNumber: number,
  priorStamp: string | null,
  options: PollStepOptions = {},
): Promise<{ step: T | null; observed: boolean }> {
  const { attempts = 24, intervalMs = 5000, sleep = defaultSleep } = options;
  let observed = false;
  for (let attempt = 0; attempt < attempts; attempt++) {
    await sleep(intervalMs);
    try {
      const steps = await fetchSteps();
      const step = (steps || []).find((st) => st.step_number === stepNumber);
      if (!step || stepStamp(step) === priorStamp) continue;
      observed = true;
      if (isFinalRunStatus(step.status)) return { step, observed };
    } catch {
      // Transient poll failure — keep waiting; the budget bounds us.
    }
  }
  return { step: null, observed };
}

/** "m:ss" for the progress line. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
